import { getLocaleString } from '@assets/i18n/config';
import { isNaN } from 'lodash-es';

export const formatMoneyValueCentsWithDecimals = (
  cents: number | undefined,
  currency = '$',
  fractionDigits = 2,
  standard = true,
): string => {
  const value = (cents ?? 0) / 100;
  const locale = getLocaleString();
  const symbolAfter = !locale.startsWith('en');

  const formatNumber = (num: number): string => {
    if (standard && num % 1 === 0) {
      const formatted = new Intl.NumberFormat(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(num);
      return formatted;
    }

    let valueStr = num.toFixed(fractionDigits).replace(/\.?0+$/, '');
    if (!valueStr.includes('.') && !valueStr.includes(',')) valueStr += '.00';
    if (valueStr.match(/\.\d$/) || valueStr.match(/,\d$/)) valueStr += '0';

    const numValue = Number(valueStr.replace(',', '.'));
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: fractionDigits,
    }).format(numValue);
  };

  const formattedNumber = formatNumber(Math.abs(value));
  return symbolAfter
    ? `${value < 0 ? '-' : ''}${formattedNumber} ${currency}`
    : `${value < 0 ? '-' : ''}${currency}${formattedNumber}`;
};

export const formatMoneyValue = (
  cents: number | undefined,
  currency = '$',
  fractionDigits = 2,
  standard = true,
  showCurrency = true,
): string => {
  const currencySymbol = showCurrency ? currency : '';
  const locale = getLocaleString();
  const symbolAfter = !locale.startsWith('en');

  if (!cents) {
    const formatted = new Intl.NumberFormat(locale, {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(0);
    return symbolAfter
      ? `${formatted} ${currencySymbol}`
      : `${currencySymbol}${formatted}`;
  }

  const value = cents / 100;
  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';

  if (standard) {
    if (value % 1 === 0) {
      const formatted = new Intl.NumberFormat(locale, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(absValue);
      return symbolAfter
        ? `${sign}${formatted} ${currencySymbol}`
        : `${sign}${currencySymbol}${formatted}`;
    }

    const formatted = new Intl.NumberFormat(locale, {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(absValue);
    return symbolAfter
      ? `${sign}${formatted} ${currencySymbol}`
      : `${sign}${currencySymbol}${formatted}`;
  }

  const formatted = new Intl.NumberFormat(locale, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(absValue);
  return symbolAfter
    ? `${sign}${formatted} ${currencySymbol}`
    : `${sign}${currencySymbol}${formatted}`;
};

export const formatPercentValue = (
  percent: number,
  fractionDigits = 2,
): string => {
  const locale = getLocaleString();

  if (isNaN(percent)) {
    return new Intl.NumberFormat(locale, {
      style: 'percent',
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(0);
  }

  return new Intl.NumberFormat(locale, {
    style: 'percent',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(percent);
};
